// Automatic Cycle Renewal Logic
import dayjs from 'dayjs';

/**
 * Check if user's cycle needs renewal and return new cycle dates
 * @param {Object} userProfile - User profile with currentCycleEnd and certifyingBody
 * @returns {Object|null} - New cycle dates if renewal needed, null otherwise
 */
export const checkCycleRenewal = (userProfile) => {
  if (!userProfile?.currentCycleEnd) {
    return null;
  }

  const today = dayjs();
  const cycleEnd = dayjs(userProfile.currentCycleEnd);
  
  // Check if cycle has ended
  if (today.isAfter(cycleEnd)) {
    // Calculate new cycle dates
    const cycleLengthYears = userProfile.certifyingBody === 'BEI' ? 5 : 4; // BEI=5 years, RID=4 years
    const newStart = cycleEnd.add(1, 'day').format('YYYY-MM-DD'); // Day after old end
    const newEnd = cycleEnd.add(cycleLengthYears, 'year').add(1, 'day').format('YYYY-MM-DD'); // Same day, X years later
    
    return {
      needsRenewal: true,
      oldCycleStart: userProfile.currentCycleStart,
      oldCycleEnd: userProfile.currentCycleEnd,
      newCycleStart: newStart,
      newCycleEnd: newEnd,
      renewalDate: today.format('YYYY-MM-DD')
    };
  }
  
  return null;
};

/**
 * Archive all CEUs from the old cycle
 * @param {string} userId - User ID
 * @param {string} oldCycleEnd - End date of old cycle
 * @param {Function} ceuService - CEU service to update records
 */
export const archiveOldCycleCEUs = async (userId, oldCycleEnd, ceuService) => {
  try {
    // Get all user's CEUs
    const result = await ceuService.getUserCEUs(userId);
    if (!result.success || !result.data) {
      return { success: false, error: 'Failed to fetch CEUs' };
    }

    const ceus = result.data;
    const oldCycleEndDate = dayjs(oldCycleEnd);
    
    // Find CEUs that belong to old cycle (date <= oldCycleEnd)
    const ceusToArchive = ceus.filter(ceu => {
      const ceuDate = dayjs(ceu.date);
      return ceuDate.isBefore(oldCycleEndDate) || ceuDate.isSame(oldCycleEndDate, 'day');
    });

    // Update each CEU to mark as archived
    for (const ceu of ceusToArchive) {
      await ceuService.updateCEU(userId, ceu.ceuId, {
        isCurrentCycle: false,
        archivedAt: new Date().toISOString()
      });
    }

    return { success: true, archivedCount: ceusToArchive.length };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
